import { LEGAL_CONTACT, LEGAL_UPDATED_AT } from "./legal-config";
import LegalPageLayout, { LegalCallout, LegalSection } from "./LegalPageLayout";

export default function PolitiqueConfidentialite() {
  return (
    <LegalPageLayout
      title="Politique de confidentialité"
      topbarTitle="Confidentialité"
      lead="Cette politique explique avec simplicité quelles données sont utilisées pour faire fonctionner votre refuge, personnaliser votre expérience et protéger votre compte."
      updatedAt={LEGAL_UPDATED_AT}
    >
      <LegalSection
        number="01"
        title="Responsable du traitement"
      >
        <p>Le responsable du traitement des données personnelles est {LEGAL_CONTACT.publisherName}, entrepreneur individuel, joignable à <a href={`mailto:${LEGAL_CONTACT.email}`}>{LEGAL_CONTACT.email}</a>.</p>
      </LegalSection>

      <LegalSection
        number="02"
        title="Données traitées"
      >
        <ul className="legal-list">
          <li><strong>Données de compte :</strong> adresse électronique, identifiant Firebase, pseudonyme et informations de profil facultatives.</li>
          <li><strong>Préférences du refuge :</strong> besoin principal, durée quotidienne choisie, favoris, ambiances, réglages et préférences renseignées volontairement.</li>
          <li><strong>Progression et usage :</strong> sessions lancées ou terminées, durée d’écoute, historique, séries, objectifs, badges et ressentis saisis volontairement.</li>
          <li><strong>Personnalisation :</strong> choix nécessaires pour permettre à Auria de préparer une expérience adaptée, sans présenter la technologie comme une finalité du service.</li>
          <li><strong>Abonnement :</strong> statut du Refuge Personnel, références Stripe, dates de début, de renouvellement ou de résiliation. Les données complètes de carte bancaire sont traitées par Stripe et ne sont pas stockées par Harmonauria.</li>
          <li><strong>Données techniques :</strong> adresse IP, journaux de sécurité, type de navigateur, appareil et informations nécessaires au fonctionnement, à la prévention des abus et au diagnostic des erreurs.</li>
          <li><strong>Contact et communication :</strong> contenu des demandes adressées au support et inscription à une lettre d’information uniquement lorsque vous l’avez choisie.</li>
        </ul>
      </LegalSection>

      <LegalSection
        number="03"
        title="Finalités et bases légales"
      >
        <ul className="legal-list">
          <li><strong>Créer et gérer votre compte, fournir les contenus et personnaliser votre refuge :</strong> exécution du contrat ou mesures précontractuelles.</li>
          <li><strong>Gérer l’essai, les paiements et l’abonnement au Refuge Personnel :</strong> exécution du contrat et obligations légales comptables.</li>
          <li><strong>Protéger le service, prévenir les abus et résoudre les incidents :</strong> intérêt légitime à assurer la sécurité et la continuité de Harmonauria.</li>
          <li><strong>Répondre à vos demandes :</strong> exécution du contrat, intérêt légitime ou obligation légale selon la nature de la demande.</li>
          <li><strong>Envoyer une lettre d’information :</strong> votre consentement, révocable à tout moment.</li>
        </ul>
      </LegalSection>

      <LegalSection
        number="04"
        title="Destinataires et sous-traitants"
        highlight
      >
        <p>Les données ne sont ni vendues ni louées à des fins commerciales.</p>
        <ul className="legal-list">
          <li><strong>Google Firebase :</strong> authentification, base de données, fonctions serveur, sécurité et hébergement.</li>
          <li><strong>Stripe :</strong> paiement, essai, facturation, renouvellement et gestion de l’abonnement.</li>
          <li><strong>OVHcloud :</strong> gestion du nom de domaine, DNS et services de messagerie associés au domaine.</li>
          <li><strong>Autorités compétentes :</strong> uniquement lorsqu’une obligation légale l’impose.</li>
        </ul>
        <LegalCallout title="Transferts hors de l’Espace économique européen">
          <p>Certains prestataires ou sous-traitants ultérieurs peuvent traiter des données en dehors de l’Espace économique européen. Ces transferts doivent être encadrés par un mécanisme reconnu, comme une décision d’adéquation ou des clauses contractuelles types.</p>
        </LegalCallout>
      </LegalSection>

      <LegalSection
        number="05"
        title="Durées de conservation"
      >
        <ul className="legal-list">
          <li><strong>Compte, préférences et progression :</strong> pendant la durée d’existence du compte, puis suppression ou anonymisation lorsque le compte est supprimé, sous réserve des sauvegardes techniques temporaires et des obligations légales.</li>
          <li><strong>Données de facturation :</strong> conservation pendant la durée légale applicable, pouvant atteindre dix ans pour les pièces comptables.</li>
          <li><strong>Demandes adressées au support :</strong> pendant le temps nécessaire au traitement, puis jusqu’à trois ans afin d’assurer le suivi des échanges et la défense des droits.</li>
          <li><strong>Lettre d’information :</strong> jusqu’au retrait du consentement ou après une période d’inactivité compatible avec la réglementation applicable.</li>
          <li><strong>Journaux techniques et de sécurité :</strong> pendant une durée limitée et proportionnée aux besoins de sécurité, de prévention des abus et de diagnostic.</li>
        </ul>
      </LegalSection>

      <LegalSection
        number="06"
        title="Vos droits"
      >
        <p>Vous pouvez demander l’accès à vos données, leur rectification, leur effacement, la limitation du traitement, leur portabilité ou vous opposer à certains traitements. Vous pouvez également retirer votre consentement à tout moment lorsqu’un traitement repose sur celui-ci.</p>
        <p>Pour exercer vos droits, écrivez à <a href={`mailto:${LEGAL_CONTACT.email}`}>{LEGAL_CONTACT.email}</a> en précisant votre demande et l’adresse électronique liée à votre compte. Une preuve d’identité peut être demandée uniquement si cela est nécessaire pour éviter une divulgation à un tiers.</p>
        <p>Vous pouvez introduire une réclamation auprès de la <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">Commission nationale de l’informatique et des libertés</a>.</p>
      </LegalSection>

      <LegalSection
        number="07"
        title="Cookies et stockage local"
      >
        <p>Harmonauria utilise des cookies ou mécanismes de stockage local strictement nécessaires à l’authentification, à la sécurité, à la mémorisation de vos préférences, au fonctionnement des sessions et à la continuité de votre refuge.</p>
        <p>Stripe peut utiliser ses propres traceurs lors du paiement ou de l’ouverture de son portail client. Harmonauria n’utilise pas de traceurs publicitaires.</p>
        <p>Tout outil de mesure d’audience ou traceur non strictement nécessaire qui serait ajouté ultérieurement devra être présenté avant son activation et soumis à votre consentement lorsque la réglementation l’exige.</p>
      </LegalSection>

      <LegalSection
        number="08"
        title="Sécurité et mise à jour"
      >
        <p>Harmonauria met en œuvre des mesures techniques et organisationnelles adaptées à la taille du service et à la nature des données traitées. Aucun système ne pouvant garantir une sécurité absolue, les mesures sont réévaluées lorsque le service évolue.</p>
        <p>Cette politique peut être mise à jour pour refléter une évolution de Harmonauria, de ses prestataires ou de la réglementation. La date affichée en haut de la page indique la version actuellement applicable.</p>
      </LegalSection>
    </LegalPageLayout>
  );
}
